import { Select, Stack, Title, Button, Alert, TagsInput, Loader } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconInfoCircle } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getUsersOfOrg } from "../../utils/api-communication/RestApiCli";
import {
  DEFAULT_LICENSE_EXPIRY_SELECTION,
  expiryIsoFromSelection,
  isLicenseExpirySelectionIncomplete,
  LicenseExpirySelection,
} from "@utils/CommonUtil";
import LicenseExpirySelect from "../common/LicenseExpirySelect";

export default function OfflineLicenseRequestForm(props) {
  const { t } = useTranslation();
  const [organizations, setOrganizations] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setOrganizations(
      props.organizations.map((org) => ({
        value: org.orgId,
        label: org.name + " (" + org.orgId + ")",
      })),
    );
  }, [props.organizations]);

  const form = useForm({
    initialValues: {
      emails: [],
      user: null,
      organization: null,
      expiresAt: DEFAULT_LICENSE_EXPIRY_SELECTION as LicenseExpirySelection,
    },

    validate: {
      emails: (value) => {
        if (value.length === 0) {
          return "Email list cannot be empty";
        }
        for (const email of value) {
          if (!/^\S+@\S+$/.test(email)) {
            return "Invalid email";
          }
        }
        return null;
      },
      user: (value) => {
        if (value === null) {
          return "User cannot be empty";
        }
        return null;
      },
      organization: (value) => {
        if (value === null) {
          return "Organization cannot be empty";
        }
        return null;
      },
      expiresAt: (value) =>
        isLicenseExpirySelectionIncomplete(value) ? t("licenseExpiry.customDateRequired") : null,
    },
  });

  useEffect(() => {
    form.setFieldValue("user", null);
    if (form.getValues().organization) {
      getUsersOfOrg({ orgId: form.getValues().organization }, (res) => {
        setUsers(
          res.data.map((user) => ({
            value: user.username,
            label: user.name + " (" + user.username + ")",
          })),
        );
      });
    }
  }, [form.getValues().organization]);

  return (
    <Stack>
      <Title style={{ color: "dimmed" }} size="h3">
        Create Offline License Request
      </Title>
      <Alert icon={<IconInfoCircle size="1rem" />} color="gray">
        To create an offline license for an organization, please select the organization and enter
        the emails of the users who will receive the license. Your request will be processed by the
        administrator.
      </Alert>
      <form
        onSubmit={form.onSubmit((values) => {
          setLoading(true);
          const expiresAt = expiryIsoFromSelection(values.expiresAt);
          props.onSubmit(
            {
              orgId: values.organization,
              username: values.user,
              emails: values.emails,
              ...(expiresAt ? { expiresAt } : {}),
            },
            setLoading,
          );
        })}
      >
        <Select
          my="sm"
          label="Select Organization"
          placeholder="Organization"
          searchable
          nothingFoundMessage={t("distributorDashboard.noOrganizationFound")}
          styles={{ option: { backgroundColor: "unset" } }}
          data={organizations}
          {...form.getInputProps("organization")}
        />
        {form.getValues().organization && (
          <Select
            my="sm"
            label="Select User"
            placeholder="User"
            styles={{ option: { backgroundColor: "unset" } }}
            data={users}
            {...form.getInputProps("user")}
          />
        )}

        <TagsInput
          data={form.values.emails}
          placeholder="Enter email"
          clearable
          {...form.getInputProps("emails")}
        />

        <LicenseExpirySelect
          my="sm"
          value={form.getValues().expiresAt}
          onChange={(value) => form.setFieldValue("expiresAt", value)}
          error={form.errors.expiresAt}
        />

        {/* <MultiSelect
          my="sm"
          label="Emails"
          data={form.values.emails}
          placeholder="Enter email"
          searchable
          creatable
          styles={{ option: { backgroundColor: "unset" } }}
          getCreateLabel={(query) => `+ Create ${query}`}
          onCreate={(query) => {
            const item = { value: query, label: query };
            form.insertListItem("emails", item);
            return item;
          }}
          {...form.getInputProps("emails")}
        /> */}

        <Button my="sm" type="submit" disabled={loading}>
          {loading ? <Loader size={"sm"} width={"100%"} /> : "Request Offline License"}
        </Button>
      </form>
    </Stack>
  );
}
